import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import BudgetUnitService from '../../services/budgetUnit.service';
import { useTranslation } from '../../hooks/useTranslation';

const initialFormState = {
  name: '',
  is_active: true
};

const BudgetUnitsConfig = () => {
  const { t } = useTranslation();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [editingUnit, setEditingUnit] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadUnits = async () => {
    setLoading(true);
    try {
      const response = await BudgetUnitService.getAll();
      setUnits(response.data?.data || []);
    } catch (error) {
      console.error('Error loading budget units:', error);
      setSnackbar({ open: true, message: t('budgets.errorLoadingUnits'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUnits();
  }, []);

  const handleOpenDialog = (unit = null) => {
    if (unit) {
      setEditingUnit(unit);
      setFormData({
        name: unit.name || '',
        is_active: unit.is_active ?? true
      });
    } else {
      setEditingUnit(null);
      setFormData(initialFormState);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData(initialFormState);
    setEditingUnit(null);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setSnackbar({ open: true, message: t('budgets.nameRequired'), severity: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      if (editingUnit) {
        await BudgetUnitService.update(editingUnit.id, {
          name: formData.name.trim(),
          is_active: formData.is_active
        });
        setSnackbar({ open: true, message: t('budgets.budgetUnitUpdated'), severity: 'success' });
      } else {
        await BudgetUnitService.create({
          name: formData.name.trim(),
          is_active: formData.is_active
        });
        setSnackbar({ open: true, message: t('budgets.budgetUnitCreated'), severity: 'success' });
      }
      handleCloseDialog();
      loadUnits();
    } catch (error) {
      console.error('Error saving budget unit:', error);
      const message = error.response?.data?.message || t('budgets.errorSavingUnit');
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (unit) => {
    if (!window.confirm(t('budgets.setInactiveConfirm', { name: unit.name }))) {
      return;
    }

    try {
      await BudgetUnitService.update(unit.id, {
        name: unit.name,
        is_active: false
      });
      setSnackbar({ open: true, message: t('budgets.budgetUnitInactive'), severity: 'success' });
      loadUnits();
    } catch (error) {
      console.error('Error updating budget unit:', error);
      const message = error.response?.data?.message || t('budgets.errorUpdatingUnit');
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  const tableContent = useMemo(() => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" py={5}>
          <CircularProgress />
        </Box>
      );
    }

    if (!units.length) {
      return (
        <Box py={5} textAlign="center">
          <Typography color="text.secondary">{t('budgets.noActiveUnits')}</Typography>
        </Box>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('common.name')}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {units.map((unit) => (
              <TableRow key={unit.id} hover>
                <TableCell>{unit.name}</TableCell>
                <TableCell align="right">
                  <Tooltip title={t('common.edit')}>
                    <IconButton onClick={() => handleOpenDialog(unit)} size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('budgets.markInactive')}>
                    <IconButton onClick={() => handleDeactivate(unit)} size="small" color="error">
                      <ArchiveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }, [loading, units, t]);

  return (
    <Box p={2}>
      <Card>
        <CardHeader
          title={t('budgets.budgetUnits')}
          subheader={t('budgets.manageUnits')}
          action={
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadUnits}
              >
                {t('budgets.refresh')}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                {t('budgets.newUnit')}
              </Button>
            </Box>
          }
        />
        <CardContent>
          {tableContent}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUnit ? t('budgets.editBudgetUnit') : t('budgets.newBudgetUnit')}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label={t('common.name')}
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  color="primary"
                />
              }
              label={t('budgets.active')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? t('budgets.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BudgetUnitsConfig;

